import { AppContextProps } from '../hooks/useApp'
import { CHUNK_MAX_ESTIMATE } from '../types/constants'
import { Device, DeviceHwID, ID, Port, DeviceValues } from '../types/models'
import { ConfigDiff, FwInfoPort, PortConfig, PortNumber, PortType, UncommittedConfig } from '../types/ports'
import { DeviceCollection } from './DeviceCollection'
import { SystemControl } from './SystemControl'
import { PortControl } from './ports/PortControl'
import { callAxios } from '../hooks/useAxios'

/**
 * A container for managing ports.
 */
export class DeviceControl {

  system: SystemControl
  device: Device
  portControls: Record<string, PortControl<Port, PortConfig>[]>
  portControlsById: Record<ID, PortControl<Port, PortConfig>>
  portGroupById: Record<ID, string>

  constructor(system: SystemControl, device: Device) {
    this.system = system
    this.device = device

    this.portControls = {}
    this.portControlsById = {}
    this.portGroupById = {}
  }

  get app(): AppContextProps {
    return this.system.app
  }

  /**
   * Access to device collection.
   */
  get collection(): DeviceCollection {
    return this.system.collection
  }

  refresh(): void {
    this.system.refresh()
  }

  /**
   * Set port controls and their grouping and numbering.
   */
  setPortControls(portControls: PortControl<Port, PortConfig>[]) {
    this.portControls = {}
    this.portControlsById = {}

    const orderingCount: Record<string, number> = {}

    const sortPortControls = (a: PortControl<Port, PortConfig>, b: PortControl<Port, PortConfig>) => {
      if (a.port.port_number < b.port.port_number) {
        return -1
      }
      if (a.port.port_number > b.port.port_number) {
        return 1
      }
      return 0
    }
    portControls.sort(sortPortControls)

    for (const control of portControls) {
      this.portControls[control.group] = this.portControls[control.group] || []
      this.portControls[control.group].push(control)
      this.portControlsById[control.port.id as ID] = control
      this.portGroupById[control.port.id as ID] = control.group
      const orderingKey = control.port.type
      orderingCount[orderingKey] = (orderingCount[orderingKey] || 0) + 1
      control.orderNumber = orderingCount[orderingKey]
    }
  }

  /**
   * Get the names of non-empty groups in canonical order.
   */
  getGroups(): string[] {
    return [
      this.app.t('PortGroup-Energy Metering'),
      this.app.t('PortGroup-Energy Production'),
      this.app.t('PortGroup-Relays'),
      this.app.userHas('DEVICE_OPTIMIZER') ? this.app.t('PortGroup-Optimization') : '',
      this.app.t('PortGroup-Info'),
      this.app.t('PortGroup-Other'),
    ].filter(g => !!this.portControls[g])
  }

  /**
   * Find the group name of the port.
   */
  getGroupOf(portId: ID): string {
    return this.portGroupById[portId]
  }

  /**
   * Get the port controls from the group.
   */
  getPortControls(group: string): PortControl<Port, PortConfig>[] {
    return this.portControls[group] || []
  }

  /**
   * Get all port controls.
   */
  getAllPortControls(): PortControl<Port, PortConfig>[] {
    return Object.values(this.portControls).reduce((prev, cur) => prev.concat(cur), [])
  }

  /**
   * Find the group name that have the specific port.
   */
  findGroupOfPort(portId: ID): string | null {
    for (const [name, controls] of Object.entries(this.portControls)) {
      for (const control of controls) {
        if (control.port.id === portId) {
          return name
        }
      }
    }
    return null
  }

  /**
   * Coerce a config value to the type of its default. Text inputs hold the raw
   * string while the user is typing and only convert on blur; a save that runs
   * before the blur handler would otherwise flash a string where the server-side
   * binary encoder needs a number, making the server reject the whole batch.
   */
  static coerceConfigValue(value: unknown, defaultValue: unknown): unknown {
    if (typeof defaultValue === 'number' && typeof value === 'string') {
      const num = parseFloat(value.replace(',', '.'))
      return Number.isNaN(num) ? defaultValue : num
    }
    if (Array.isArray(defaultValue) && Array.isArray(value)) {
      return value.map((v, i) =>
        DeviceControl.coerceConfigValue(v, defaultValue[Math.min(i, defaultValue.length - 1)]))
    }
    return value
  }

  /**
   * Build a single flash item from a port control's defaults and current config.
   * Uses getConfigDefaults() as the schema and getConfig() to resolve values.
   */
  static buildPortFlashItem(control: PortControl<Port, PortConfig>): UncommittedConfig {
    const defaults = control.getConfigDefaults() as Record<string, unknown>
    const config: Record<string, unknown> = {}
    for (const key of Object.keys(defaults)) {
      const val = control.getConfig(key)
      config[key] = DeviceControl.coerceConfigValue(val !== undefined ? val : defaults[key], defaults[key])
    }
    return {
      id: control.port.id as ID,
      port: control.port.port_number,
      config: control.normalizeFlashConfig(config) as PortConfig,
    }
  }

  /**
   * Find a port control by port number.
   */
  findPort(number: PortNumber): PortControl<Port, PortConfig> {
    const portControl = this.findPortBy((portControl) => portControl.port.port_number === number)
    if (portControl) return portControl
    throw new Error(`Cannot find the port contoller for port number ${number}.`)
  }

  /**
   * Find a port control by filtering function.
   */
  findPortBy(filter: (p: PortControl<Port, PortConfig>) => boolean): PortControl<Port, PortConfig> | null {
    for (const group of Object.values(this.portControls)) {
      for (const portControl of group) {
        if (filter(portControl)) {
          return portControl
        }
      }
    }
    return null
  }

  /**
   * Find all port controls by filtering function.
   */
  findPortsBy(filter: (p: PortControl<Port, PortConfig>) => boolean): PortControl<Port, PortConfig>[] {
    const ports: PortControl<Port, PortConfig>[] = []
    for (const group of Object.values(this.portControls)) {
      for (const portControl of group) {
        if (filter(portControl)) {
          ports.push(portControl)
        }
      }
    }
    return ports
  }

  /**
   * Find the first port matching the type.
   */
  findPortByType(t: PortType): PortControl<Port, PortConfig> | null {
    return this.findPortBy(p => p.port.type === t)
  }

  /**
   * Find the port witht the port number.
   */
  findPortByNumber(num: PortNumber): PortControl<Port, PortConfig> | null {
    return this.findPortBy(p => p.port.port_number === num)
  }

  /**
   * Get the port control by port.
   */
  portControlFor(port: Port): PortControl<Port, PortConfig> {
    return this.portControlsById[port.id as ID]
  }

  /**
   * Get the port control by port ID.
   */
  portControlForId(id: ID): PortControl<Port, PortConfig> {
    return this.portControlsById[id]
  }

  /**
   * Check the configuration differencies for possible problems for any port.
   */
  hasProblems(): boolean {
    for (const portCtrl of Object.values(this.portControlsById)) {
      const diff = portCtrl.hardwareDiff()
      if (diff.type === 'DIFFERS' || diff.type === 'READ FAILED') {
        return true
      }
    }
    return false
  }

  /**
   * Construct description of differencies between set configuration and hardware reported configuration.
   */
  hardwareDiff(): ConfigDiff {
    const ret: ConfigDiff = {}
    Object.values(this.portControlsById).forEach(portCtrl => {
      ret[portCtrl.port.port_number] = portCtrl.hardwareDiff()
    })
    return ret
  }

  /**
   * Check if the device is busy running firmware upgrade.
   */
  isUpgrading(): boolean {
    const firmware: FwInfoPort | undefined = this.device.ports?.find(p => p.type === 'FW_INFO') as FwInfoPort
    return firmware && firmware.values && !!firmware.values.flag_dfu_started
  }

  /**
   * Get the percentage and device HW ID.
   */
  upgradeProgress(): [DeviceHwID, number] {
    const firmware: FwInfoPort | undefined = this.device.ports?.find(p => p.type === 'FW_INFO') as FwInfoPort
    const missingChunk = firmware.values.dfu_first_missing_chunk || 0
    const newProtocol = firmware.values.flag_new_protocol
    let progress
    if (newProtocol) {
      progress = (firmware.values.dfu_n_received_chunks || 0) / (Math.max(1, firmware.values.dfu_n_chunks || 0))
    } else {
      progress = Math.min(1, missingChunk / CHUNK_MAX_ESTIMATE)
    }

    return [this.device.hw_id, Math.round(progress * 100)]
  }

  /**
   * Calculate how many measuring ports is participating to net sum calculation.
   * @returns
   */
  countSummers(): number {
    let sum = 0
    for (const portCtrl of Object.values(this.portControlsById)) {
      if (portCtrl.isSummer()) {
        sum++
      }
    }
    return sum
  }

  /**
   * Send new values for device.
   */
  async saveDeviceValues(values: DeviceValues): Promise<boolean> {
    const res = await callAxios({
      method: 'PATCH',
      url: `/devices/${this.device.id}`,
    }, { values: { ...this.device.values, ...values } })

    if (!res.success) {
      this.app.addError(res)
    }

    return res.success
  }
}
